/**
 * The idea roll's sentence shapes (components/IdeaRoll.tsx, well/product/33
 * §9d). Every one starts "Me and [who]"; what comes after follows the thing
 * they said they want to make a moment earlier (intro-copy KINDS): a podcast
 * is "Me and [co-host] talking about [what] [every when]", a café "Me and
 * [who] [where], running a café that only sells [what]".
 *
 * Three blanks each, always in the same three roles, because the postcard
 * draws by role: `who` is the figure beside you, `where` the backdrop, `doing`
 * the thing in front of you. A role keeps its role whatever its words say
 * ("every Friday night" is a backdrop). The rows, and the levels that unlock
 * them, go in the sentence's order, who always first. Every rolled option
 * names its picture piece; one that has none falls back to the shape's own
 * (`art`).
 *
 * Owner, 2026-10-27: "let's try to have the sentence structures be slightly
 * more dependent on the prior options". Reviewed on the browse page
 * (scripts/idea-shapes, the artifact named there), whose notes come back here.
 */
import type { RollOption, RollSlot } from './imagine-copy';

export interface RollShape {
  id: string;
  /** what the browse page calls it */
  name: string;
  /** the sentence, with {who}, {where} and {doing} in it, in any order; always "Me and {who}…". The rows, and the levels that unlock them, follow its order. */
  template: string;
  /** the row labels, short and upper-cased on screen */
  labels: Record<RollSlot, string>;
  /** what one roll of a row deals, for the buttons and lines that name it: "Roll a co-host", "Now roll a day." */
  noun: Record<RollSlot, string>;
  /**
   * The little word put in front of a written where that has none of its own
   * ("my kitchen" → "in my kitchen"); "in" unless said. The rolled ones carry
   * their own ("on top of a mountain"), since no one word fits every place.
   */
  lead?: string;
  /** what an unlocked row asks for, once emptied */
  ask: Record<RollSlot, string>;
  /** the line under the path on the level that unlocks the row */
  turn: Record<RollSlot, string>;
  options: { where: readonly RollOption[]; doing: readonly RollOption[] };
  /** the picture piece for a role whose words have none of their own */
  art: { where: string; doing: string };
  /** the where is a place (it fills the film's `somewhere`); "every Friday night" is not */
  place: boolean;
}

/** Who is with you: the same eight everywhere, each with a figure on the postcard. */
export const ROLL_WHO: readonly RollOption[] = [
  { text: 'my best friend', art: 'friend' },
  { text: 'Grandma Ruth', art: 'granny' },
  { text: 'my ten-year-old self', art: 'kid' },
  { text: 'a dog called Biscuit', art: 'dog' },
  { text: 'a robot named Otto', art: 'robot' },
  { text: 'a ghost called Pip', art: 'ghost' },
  { text: 'Kraben', art: 'kraben' },
  { text: 'a pirate called June', art: 'pirate' },
];

/** Somewhere: each a backdrop of its own (imagine-scenes.ts), each with its own little word in front ("on top of", "at", "in"), because no one word fits every place. */
export const PLACES: readonly RollOption[] = [
  { text: 'on top of a mountain', art: 'peaks' },
  { text: 'at the bottom of the sea', art: 'fish' },
  { text: 'at my old school', art: 'school' },
  { text: 'in a lighthouse on a tiny island', art: 'island' },
  { text: 'in the middle of a forest', art: 'trees' },
  { text: 'at a base on the moon', art: 'moon' },
  { text: 'in a castle in the clouds', art: 'clouds' },
  { text: 'at an oasis in the desert', art: 'dunes' },
  { text: 'at a bus stop in the rain', art: 'rain' },
  { text: 'on a rooftop at midnight', art: 'stars' },
  { text: 'on the beach at dawn', art: 'sun' },
  { text: 'in a village in a snow globe', art: 'dome' },
];

const WHO_ASK = 'a name, real or made up';
const WHO_TURN = 'Your turn: who is it really? Type a name.';
const WHERE_ASK = 'somewhere, like “in my kitchen”';
const WHERE_TURN = 'Your turn: where are you really?';
const SOMEONE = 'someone';
const A_PLACE = 'a place';

export const SHAPES: readonly RollShape[] = [
  {
    id: 'anything', name: 'Anything at all',
    template: 'Me and {who} {where}, {doing}',
    labels: { who: 'Who', where: 'Where', doing: 'Doing what' },
    noun: { who: SOMEONE, where: A_PLACE, doing: 'something to do' },
    ask: { who: WHO_ASK, where: WHERE_ASK, doing: 'doing what? like “learning to swim”' },
    turn: { who: WHO_TURN, where: WHERE_TURN, doing: 'Your turn: what are you really doing?' },
    options: { where: PLACES, doing: [
      { text: 'teaching a crow to talk', art: 'crow' },
      { text: 'flying a kite', art: 'kite' },
      { text: 'starting a band', art: 'guitar' },
      { text: 'baking a cake the size of a car', art: 'cake' },
      { text: 'digging for treasure', art: 'chest' },
      { text: 'planning a heist', art: 'map' },
      { text: 'racing snails', art: 'snails' },
      { text: 'launching a paper rocket', art: 'rocket' },
    ] },
    art: { where: 'clouds', doing: 'kite' }, place: true,
  },
  {
    id: 'story', name: 'A story (novel, short stories, a world)',
    template: 'Me and {who} {where}, the day {doing}',
    labels: { who: 'Who', where: 'Where', doing: 'The day…' },
    noun: { who: SOMEONE, where: A_PLACE, doing: 'what happened' },
    ask: { who: WHO_ASK, where: WHERE_ASK, doing: 'what happened? like “the sea froze”' },
    turn: { who: WHO_TURN, where: WHERE_TURN, doing: 'Your turn: what happened that day?' },
    options: { where: PLACES, doing: [
      { text: 'the sea froze over', art: 'book' },
      { text: 'the moon went missing', art: 'book' },
      { text: 'every clock stopped', art: 'book' },
      { text: 'the crows started talking', art: 'crow' },
      { text: 'a stranger left us a map', art: 'map' },
      { text: 'we found the buried chest', art: 'chest' },
    ] },
    art: { where: 'trees', doing: 'book' }, place: true,
  },
  {
    id: 'memoir', name: 'A memoir or essays',
    template: 'Me and {who} {where}, the summer we {doing}',
    labels: { who: 'Who', where: 'Where', doing: 'The summer we…' },
    noun: { who: SOMEONE, where: A_PLACE, doing: 'what you did' },
    ask: { who: WHO_ASK, where: 'somewhere you have been', doing: 'what did you do? like “learned to swim”' },
    turn: { who: WHO_TURN, where: 'Your turn: where were you really?', doing: 'Your turn: what did you do that summer?' },
    options: { where: PLACES, doing: [
      { text: 'ran away for one afternoon', art: 'map' },
      { text: 'learned to swim', art: 'book' },
      { text: 'built a raft that sank', art: 'workbench' },
      { text: 'sold lemonade to strangers', art: 'stall' },
      { text: 'started a band', art: 'guitar' },
      { text: 'flew a kite too high', art: 'kite' },
    ] },
    art: { where: 'sun', doing: 'book' }, place: true,
  },
  {
    id: 'poems', name: 'Poems',
    template: 'Me and {who} {where}, writing a poem about {doing}',
    labels: { who: 'Who', where: 'Where', doing: 'About' },
    noun: { who: SOMEONE, where: A_PLACE, doing: 'a topic' },
    ask: { who: WHO_ASK, where: WHERE_ASK, doing: 'about what? like “the last bus home”' },
    turn: { who: WHO_TURN, where: WHERE_TURN, doing: 'Your turn: what is the poem about?' },
    options: { where: PLACES, doing: [
      { text: 'the last bus home', art: 'book' },
      { text: 'a lost red glove', art: 'book' },
      { text: 'Tuesday’s rain', art: 'book' },
      { text: 'the moon’s day off', art: 'book' },
      { text: 'my grandmother’s hands', art: 'book' },
      { text: 'the smell of toast', art: 'book' },
    ] },
    art: { where: 'stars', doing: 'book' }, place: true,
  },
  {
    id: 'newsletter', name: 'A newsletter or a blog',
    template: 'Me and {who} writing to {where} about {doing}',
    labels: { who: 'Who', where: 'Readers', doing: 'About' },
    noun: { who: SOMEONE, where: 'readers', doing: 'a topic' },
    lead: '',
    ask: { who: WHO_ASK, where: 'who reads it? like “my whole street”', doing: 'about what? like “tiny museums”' },
    turn: { who: WHO_TURN, where: 'Your turn: who is it really for?', doing: 'Your turn: what is it really about?' },
    options: { where: [
      { text: 'a thousand strangers', art: 'stars' },
      { text: 'everyone on my street', art: 'school' },
      { text: 'people who can’t sleep', art: 'moon' },
      { text: 'my future self', art: 'clouds' },
      { text: 'one person in Ohio', art: 'dunes' },
      { text: 'the whole snow-globe village', art: 'dome' },
    ], doing: [
      { text: 'the best soup in town', art: 'typewriter' },
      { text: 'tiny museums', art: 'typewriter' },
      { text: 'why cats sit in boxes', art: 'typewriter' },
      { text: 'learning to draw at forty', art: 'easel' },
      { text: 'bus stops of the world', art: 'typewriter' },
      { text: 'one new word a week', art: 'book' },
    ] },
    art: { where: 'stars', doing: 'typewriter' }, place: false,
  },
  {
    id: 'podcast', name: 'A podcast',
    template: 'Me and {who} talking about {doing} {where}',
    labels: { who: 'Co-host', where: 'When', doing: 'About' },
    noun: { who: 'a co-host', where: 'a day', doing: 'a topic' },
    lead: 'every',
    ask: { who: 'a name, real or made up', where: 'when? like “Sunday morning”', doing: 'about what? like “ghosts we have met”' },
    turn: { who: 'Your turn: who is your co-host really? Type a name.', where: 'Your turn: when is it really on?', doing: 'Your turn: what do you really talk about?' },
    options: { where: [
      { text: 'every Friday night', art: 'stars' },
      { text: 'every Sunday morning', art: 'sun' },
      { text: 'every rainy Tuesday', art: 'rain' },
      { text: 'every full moon', art: 'moon' },
      { text: 'every snow day', art: 'dome' },
      { text: 'every school lunch', art: 'school' },
    ], doing: [
      { text: 'ghosts we have met', art: 'mic' },
      { text: 'the worst films ever made', art: 'mic' },
      { text: 'what our grandparents got right', art: 'mic' },
      { text: 'tiny inventions', art: 'mic' },
      { text: 'snacks from around the world', art: 'mic' },
      { text: 'mysteries nobody solved', art: 'mic' },
    ] },
    art: { where: 'stars', doing: 'mic' }, place: false,
  },
  {
    id: 'channel', name: 'A channel (YouTube, TikTok, Instagram, a web series)',
    template: 'Me and {who} filming {doing} {where}',
    labels: { who: 'Who', where: 'Where', doing: 'Filming' },
    noun: { who: SOMEONE, where: A_PLACE, doing: 'a show' },
    ask: { who: WHO_ASK, where: WHERE_ASK, doing: 'filming what? like “a cooking show”' },
    turn: { who: WHO_TURN, where: 'Your turn: where do you really film it?', doing: 'Your turn: what are you really filming?' },
    options: { where: PLACES, doing: [
      { text: 'a cooking show', art: 'phone' },
      { text: 'a show about tiny houses', art: 'phone' },
      { text: 'a dance class for robots', art: 'phone' },
      { text: 'a weekly mystery', art: 'phone' },
      { text: 'a live snail race', art: 'snails' },
      { text: 'a cake challenge', art: 'cake' },
    ] },
    art: { where: 'stars', doing: 'phone' }, place: true,
  },
  {
    id: 'film', name: 'A film (short, feature, music video, animation)',
    template: 'Me and {who} {where}, shooting {doing}',
    labels: { who: 'Who', where: 'Where', doing: 'Shooting' },
    noun: { who: SOMEONE, where: A_PLACE, doing: 'a film' },
    ask: { who: WHO_ASK, where: WHERE_ASK, doing: 'shooting what? like “a monster movie”' },
    turn: { who: WHO_TURN, where: 'Your turn: where are you really shooting?', doing: 'Your turn: what are you really shooting?' },
    options: { where: PLACES, doing: [
      { text: 'a monster movie', art: 'camera' },
      { text: 'a car chase on bikes', art: 'camera' },
      { text: 'a musical about snails', art: 'snails' },
      { text: 'a heist film', art: 'map' },
      { text: 'a love story for robots', art: 'camera' },
      { text: 'a music video for Grandma', art: 'guitar' },
    ] },
    art: { where: 'stars', doing: 'camera' }, place: true,
  },
  {
    id: 'documentary', name: 'A documentary',
    template: 'Me and {who} {where}, finding out why {doing}',
    labels: { who: 'Who', where: 'Where', doing: 'Why' },
    noun: { who: SOMEONE, where: A_PLACE, doing: 'a question' },
    ask: { who: WHO_ASK, where: WHERE_ASK, doing: 'why what? like “the bakery closed”' },
    turn: { who: WHO_TURN, where: 'Your turn: where are you really?', doing: 'Your turn: what do you really want to find out?' },
    options: { where: PLACES, doing: [
      { text: 'cats knock things over', art: 'camera' },
      { text: 'the bakery closed', art: 'camera' },
      { text: 'nobody swims here any more', art: 'camera' },
      { text: 'the clock tower stopped', art: 'camera' },
      { text: 'pigeons love this bench', art: 'camera' },
      { text: 'the lights hum at night', art: 'camera' },
    ] },
    art: { where: 'school', doing: 'camera' }, place: true,
  },
  {
    id: 'painting', name: 'Paintings, illustrations, prints, a sketchbook',
    template: 'Me and {who} {where}, painting {doing}',
    labels: { who: 'Who', where: 'Where', doing: 'Painting' },
    noun: { who: SOMEONE, where: A_PLACE, doing: 'a subject' },
    ask: { who: WHO_ASK, where: WHERE_ASK, doing: 'painting what? like “every dog in the park”' },
    turn: { who: WHO_TURN, where: 'Your turn: where are you really painting?', doing: 'Your turn: what are you really painting?' },
    options: { where: PLACES, doing: [
      { text: 'the sea, very big', art: 'easel' },
      { text: 'portraits of strangers', art: 'easel' },
      { text: 'the view at six in the morning', art: 'easel' },
      { text: 'every dog in the park', art: 'easel' },
      { text: 'a map of my dreams', art: 'map' },
      { text: 'a cake too pretty to eat', art: 'cake' },
    ] },
    art: { where: 'sun', doing: 'easel' }, place: true,
  },
  {
    id: 'comic', name: 'A comic',
    template: 'Me and {who} {where}, drawing a comic about {doing}',
    labels: { who: 'Who', where: 'Where', doing: 'About' },
    noun: { who: SOMEONE, where: A_PLACE, doing: 'a topic' },
    ask: { who: WHO_ASK, where: WHERE_ASK, doing: 'about what? like “a crab who runs a diner”' },
    turn: { who: WHO_TURN, where: WHERE_TURN, doing: 'Your turn: what is the comic really about?' },
    options: { where: PLACES, doing: [
      { text: 'a crab who runs a diner', art: 'book' },
      { text: 'a superhero who is always late', art: 'book' },
      { text: 'a haunted bus stop', art: 'book' },
      { text: 'a robot learning to cry', art: 'book' },
      { text: 'two snails in a race', art: 'snails' },
      { text: 'a pirate afraid of water', art: 'book' },
    ] },
    art: { where: 'stars', doing: 'book' }, place: true,
  },
  {
    id: 'mural', name: 'A mural',
    template: 'Me and {who} painting {doing} {where}',
    labels: { who: 'Who', where: 'Which wall', doing: 'Painting' },
    noun: { who: SOMEONE, where: 'a wall', doing: 'a picture' },
    lead: 'on',
    ask: { who: WHO_ASK, where: 'on what? like “the side of my old school”', doing: 'painting what? like “a whale”' },
    turn: { who: WHO_TURN, where: 'Your turn: what wall is it really?', doing: 'Your turn: what are you really painting?' },
    options: { where: [
      { text: 'on the side of my old school', art: 'school' },
      { text: 'on a lighthouse', art: 'island' },
      { text: 'on the bottom of a swimming pool', art: 'fish' },
      { text: 'on a water tower in the desert', art: 'dunes' },
      { text: 'on the wall of the moon-base canteen', art: 'moon' },
      { text: 'on a castle wall', art: 'clouds' },
    ], doing: [
      { text: 'a whale the size of a bus', art: 'easel' },
      { text: 'everyone on our street', art: 'easel' },
      { text: 'a sunrise that never ends', art: 'easel' },
      { text: 'a garden of giant flowers', art: 'easel' },
      { text: 'a cat as tall as a house', art: 'easel' },
      { text: 'a map of the whole town', art: 'map' },
    ] },
    art: { where: 'school', doing: 'easel' }, place: true,
  },
  {
    id: 'shop', name: 'A shop, a brand, a product, a studio',
    template: 'Me and {who} opening {doing} {where}',
    labels: { who: 'Who', where: 'Where', doing: 'Opening' },
    noun: { who: SOMEONE, where: A_PLACE, doing: 'a shop' },
    ask: { who: WHO_ASK, where: WHERE_ASK, doing: 'opening what? like “a tiny shop”' },
    turn: { who: WHO_TURN, where: 'Your turn: where is it really?', doing: 'Your turn: what are you really opening?' },
    options: { where: PLACES, doing: [
      { text: 'a tiny shop', art: 'stall' },
      { text: 'a lemonade stand for pirates', art: 'stall' },
      { text: 'a bike repair stand', art: 'workbench' },
      { text: 'a shop that only sells hats', art: 'stall' },
      { text: 'a studio for painting dogs', art: 'easel' },
      { text: 'a market stall for odd socks', art: 'stall' },
    ] },
    art: { where: 'sun', doing: 'stall' }, place: true,
  },
  {
    id: 'cafe', name: 'A café',
    template: 'Me and {who} {where}, running a café that only sells {doing}',
    labels: { who: 'Who', where: 'Where', doing: 'Only sells' },
    noun: { who: SOMEONE, where: A_PLACE, doing: 'a menu' },
    ask: { who: WHO_ASK, where: WHERE_ASK, doing: 'only sells what? like “soup for poets”' },
    turn: { who: WHO_TURN, where: 'Your turn: where is the café really?', doing: 'Your turn: what does it really sell?' },
    options: { where: PLACES, doing: [
      { text: 'pancakes', art: 'cake' },
      { text: 'soup for poets', art: 'stall' },
      { text: 'cake the size of a car', art: 'cake' },
      { text: 'tea for ghosts', art: 'stall' },
      { text: 'breakfast at midnight', art: 'stall' },
      { text: 'lemonade', art: 'stall' },
    ] },
    art: { where: 'sun', doing: 'stall' }, place: true,
  },
  {
    id: 'app', name: 'An app',
    template: 'Me and {who} {where}, building an app that {doing}',
    labels: { who: 'Who', where: 'Where', doing: 'What it does' },
    noun: { who: SOMEONE, where: A_PLACE, doing: 'what it does' },
    ask: { who: WHO_ASK, where: WHERE_ASK, doing: 'that does what? like “finds lost socks”' },
    turn: { who: WHO_TURN, where: WHERE_TURN, doing: 'Your turn: what does it really do?' },
    options: { where: PLACES, doing: [
      { text: 'finds lost socks', art: 'phone' },
      { text: 'tells you when to water plants', art: 'phone' },
      { text: 'translates dog barks', art: 'phone' },
      { text: 'plans the perfect picnic', art: 'phone' },
      { text: 'counts the stars for you', art: 'phone' },
      { text: 'finds a friend to walk with', art: 'phone' },
    ] },
    art: { where: 'moon', doing: 'workbench' }, place: true,
  },
  {
    id: 'game', name: 'A game',
    template: 'Me and {who} making a game set {where}, where you {doing}',
    labels: { who: 'Who', where: 'Setting', doing: 'What you do' },
    noun: { who: SOMEONE, where: 'a setting', doing: 'what you do' },
    ask: { who: WHO_ASK, where: WHERE_ASK, doing: 'you do what? like “race snails”' },
    turn: { who: WHO_TURN, where: 'Your turn: where is the game really set?', doing: 'Your turn: what do you really do in it?' },
    options: { where: PLACES, doing: [
      { text: 'race snails', art: 'snails' },
      { text: 'run a bakery for monsters', art: 'cake' },
      { text: 'dig for treasure', art: 'chest' },
      { text: 'fly a kite through a storm', art: 'kite' },
      { text: 'build a robot from junk', art: 'workbench' },
      { text: 'teach a crow to talk', art: 'crow' },
    ] },
    art: { where: 'stars', doing: 'workbench' }, place: true,
  },
];

/** Which shape answers which kind (intro-copy KINDS ids, as `dream:kind`), else which dream; else "anything". */
export const SHAPE_FOR: Record<string, string> = {
  'writer:novel': 'story', 'writer:short-stories': 'story', 'writer:memoir': 'memoir', 'writer:essays': 'memoir', 'writer:poems': 'poems', 'writer:newsletter': 'newsletter',
  'artist:paintings': 'painting', 'artist:illustrations': 'painting', 'artist:prints': 'painting', 'artist:sketchbook': 'painting', 'artist:comic': 'comic', 'artist:mural': 'mural',
  'filmmaker:short-film': 'film', 'filmmaker:feature': 'film', 'filmmaker:music-video': 'film', 'filmmaker:animation': 'film', 'filmmaker:documentary': 'documentary', 'filmmaker:web-series': 'channel',
  'influencer:youtube': 'channel', 'influencer:tiktok': 'channel', 'influencer:instagram': 'channel', 'influencer:podcast': 'podcast', 'influencer:newsletter': 'newsletter', 'influencer:blog': 'newsletter',
  'creator:youtube': 'channel', 'creator:podcast': 'podcast', 'creator:zine': 'comic', 'creator:newsletter': 'newsletter', 'creator:game': 'game', 'creator:blog': 'newsletter',
  'business:brand': 'shop', 'business:shop': 'shop', 'business:product': 'shop', 'business:studio': 'shop', 'business:cafe': 'cafe', 'business:app': 'app',
  'dreamer:story': 'story', 'dreamer:world': 'story', 'dreamer:game': 'game',
  writer: 'story', artist: 'painting', filmmaker: 'film', influencer: 'channel', business: 'shop', creator: 'channel',
};

export const shapeById = (id?: string): RollShape => SHAPES.find((s) => s.id === id) ?? SHAPES[0];
